import React from "react";
import { X, LinkedinLogo, Copy, MapPin, Buildings, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { scoreColor, statusColor } from "@/lib/constants";

const copy = (text, label) => {
  if (!text) return toast.error(`No ${label} available`);
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-zinc-100">
    <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">{label}</span>
    <span className="text-sm text-zinc-800 text-right">{value || "—"}</span>
  </div>
);

export default function ContactPanel({ person, onClose }) {
  if (!person) return null;
  const p = person;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="contact-panel">
      <div className="absolute inset-0 bg-zinc-900/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full border-l border-zinc-200 shadow-2xl overflow-y-auto animate-fade-up">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 h-16 flex items-center justify-between z-10">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Contact Details</span>
          <button data-testid="close-panel" onClick={onClose} className="text-zinc-400 hover:text-zinc-900 transition-colors"><X size={20} /></button>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-4 mb-6">
            {p.photo ? (
              <img src={p.photo} alt="" className="w-16 h-16 rounded-xl object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-zinc-900 text-white flex items-center justify-center font-head text-xl font-bold">
                {p.first_name?.[0]}{p.last_name?.[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-head text-xl font-bold tracking-tight">{p.name}</h3>
              <p className="text-sm text-zinc-600">{p.title}</p>
              <div className="flex items-center gap-1.5 mt-1 text-xs text-zinc-400">
                <Buildings size={12} /> {p.company} · <MapPin size={12} /> {p.location}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-6 p-4 rounded-lg bg-zinc-50 border border-zinc-200">
            <div className="relative w-14 h-14 flex items-center justify-center">
              <svg className="w-14 h-14 -rotate-90">
                <circle cx="28" cy="28" r="24" fill="none" stroke="#e4e4e7" strokeWidth="4" />
                <circle cx="28" cy="28" r="24" fill="none" stroke={scoreColor(p.hiring_score)} strokeWidth="4"
                  strokeDasharray={`${(p.hiring_score / 100) * 150.8} 150.8`} strokeLinecap="round" />
              </svg>
              <span className="absolute font-head font-bold text-sm">{p.hiring_score}</span>
            </div>
            <div>
              <p className="text-sm font-semibold">Hiring Relevance Score</p>
              <p className="text-xs text-zinc-500">Out of 100</p>
            </div>
          </div>

          <div className="mb-6 p-4 rounded-lg border border-blue-100 bg-blue-50/50">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Sparkle size={14} className="text-blue-600" weight="fill" />
              <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">Why this person</span>
            </div>
            <p className="text-sm text-zinc-700 leading-relaxed">{p.reason}</p>
          </div>

          <div className="mb-6">
            <Row label="Department" value={p.department} />
            <Row label="Seniority" value={p.seniority} />
            <Row label="Email" value={p.email} />
            <Row label="Email Status" value={p.email_status ? `${p.email_status}${p.email_confidence ? ` · ${p.email_confidence}%` : ""}` : "—"} />
            <Row label="Data Source" value={(p.sources || [p.data_source]).join(", ")} />
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <button data-testid="copy-email" onClick={() => copy(p.email, "Email")}
              className="flex items-center justify-center gap-2 h-10 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50 transition-colors">
              <Copy size={16} /> Copy Email
            </button>
            <button data-testid="copy-linkedin" onClick={() => copy(p.linkedin, "LinkedIn")}
              className="flex items-center justify-center gap-2 h-10 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50 transition-colors">
              <Copy size={16} /> Copy LinkedIn
            </button>
          </div>
          <button data-testid="copy-details" onClick={() => copy(`${p.name}\n${p.title}\n${p.company}\n${p.email || ""}\n${p.linkedin || ""}`, "Contact details")}
            className="w-full flex items-center justify-center gap-2 h-10 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50 transition-colors mb-2">
            <Copy size={16} /> Copy Contact Details
          </button>
          {p.linkedin && (
            <a data-testid="view-linkedin" href={p.linkedin} target="_blank" rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 h-10 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-800 transition-colors">
              <LinkedinLogo size={16} weight="fill" /> View LinkedIn
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
